import { ApiError } from '../../api/client';
import type { ErrorContext, FieldErrors } from '../types/domain';

function getDetailValue(detail: unknown, key: string): unknown {
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return undefined;
  return (detail as Record<string, unknown>)[key];
}

function getDetailNumber(detail: unknown, key: string, fallback: number): number {
  const value = getDetailValue(detail, key);
  return typeof value === 'number' ? value : fallback;
}

const ERROR_MESSAGES: Record<string, string> = {
  // Domain: permission
  DomainPermissionDeniedError: 'Нет прав для выполнения этого действия.',
  SongArchiveForbiddenError: 'Нет прав для работы с архивом песни.',
  SongPermissionDeniedError: 'Нет прав для работы с этой песней.',
  VoicePermissionDeniedError: 'Нет прав для работы с этим голосом.',
  UserPermissionDeniedError: 'Нет прав для работы с этим пользователем.',
  BalancePermissionDeniedError: 'Нет прав для работы с балансом.',
  TariffPermissionDeniedError: 'Нет прав для работы с тарифом.',
  PaymentPermissionDeniedError: 'Нет прав для работы с этим платежом.',
  PurchasePermissionDeniedError: 'Нет прав для работы с покупкой.',
  AnonymousUserPermissionDeniedError: 'Нет прав для работы с анонимным пользователем.',

  // Domain: not found
  EntityNotFoundError: 'Запрошенный объект не найден.',
  SongNotFoundError: 'Песня не найдена.',
  SongTrackNotFoundError: 'Трек не найден.',
  VoiceNotFoundError: 'Голос не найден.',
  UserNotFoundError: 'Пользователь не найден.',
  BalanceTransactionNotFoundError: 'Транзакция баланса не найдена.',
  TariffNotFoundError: 'Тариф не найден.',
  PaymentNotFoundError: 'Платёж не найден.',
  AnonymousUserNotFoundError: 'Анонимный пользователь не найден.',

  // Domain: conflicts
  DomainConflictError: 'Конфликт данных. Попробуйте обновить страницу.',
  SongArchiveStaleError: 'Архив песни устарел. Попробуйте повторить действие.',
  VoiceForSongIsNotReadyError: 'Голос для песни ещё не готов.',
  VoiceSongReadyMissedError: 'Не удалось дождаться готовности голоса для песни.',
  WrongBalanceTypeError: 'Некорректный тип баланса.',
  SongAlreadyLinkedToUserError: 'Песня уже привязана к пользователю.',
  SongAlreadyUnlockedError: 'Песня уже разблокирована.',
  SongCompressedTracksArtifactAlreadyReadyError: 'Архив треков уже готов.',
  FileKeyAlreadyExistsError: 'Файл с таким ключом уже существует.',
  VoiceNodeRefMissedError: 'Не удалось определить узел обработки голоса.',
  UserAlreadyExistsError: 'Пользователь с таким логином уже зарегистрирован.',
  BalanceAlreadyLinkedToUserError: 'Баланс уже привязан к пользователю.',
  BalanceTransactionBelongsToAnotherBalanceError: 'Транзакция принадлежит другому балансу.',
  TariffAlreadyExistsError: 'Тариф уже существует.',
  PaymentAlreadyLinkedToUserError: 'Платёж уже привязан к пользователю.',
  UnsupportedPurchaseItemTypeError: 'Неподдерживаемый тип товара.',
  PurchaseAlreadyLinkedToUserError: 'Покупка уже привязана к пользователю.',

  // Domain: rules / state
  DomainRuleViolationError: 'Нарушение бизнес-правил. Действие недоступно.',
  GenerationTaskAwaitingGenerationForbiddenError: 'Задача генерации ожидает запуска — действие недоступно.',
  GenerationTaskDispatchForbiddenError: 'Невозможно отправить задачу в текущем статусе.',
  GenerationTaskAttemptLimitReachedError: 'Достигнут лимит попыток генерации.',
  GenerationTaskGeneratingForbiddenError: 'Невозможно выполнить действие во время генерации.',
  GenerationTaskAwaitingPreviewForbiddenError: 'Задача ожидает превью — действие недоступно.',
  GenerationTaskDispatchingForbiddenError: 'Невозможно выполнить действие во время отправки.',
  GenerationTaskDispatchingReleaseForbiddenError: 'Невозможно освободить задачу в текущем статусе.',
  GenerationTaskMakingPreviewForbiddenError: 'Невозможно выполнить действие во время создания превью.',
  GenerationTaskReleaseMakingPreviewForbiddenError: 'Невозможно освободить задачу во время создания превью.',
  GenerationTaskFinishForbiddenError: 'Невозможно завершить задачу в текущем статусе.',
  GenerationTaskFailForbiddenError: 'Невозможно пометить задачу как неудачную в текущем статусе.',
  GenerationTaskCancelForbiddenError: 'Невозможно отменить задачу в текущем статусе.',
  SongAdditionalGenerationForbiddenError: 'Дополнительная генерация для этой песни сейчас недоступна.',
  SongFailureForbiddenError: 'Пометить песню как неудачную в текущем статусе нельзя.',
  SongArchiveEmptyError: 'В песне нет треков для архивации.',
  SongReadyForbiddenError: 'Песня не может быть переведена в готовое состояние сейчас.',
  SongAwaitingGenerationForbiddenError: 'Песня ожидает генерации — действие недоступно.',
  SongStartGenerationForbiddenError: 'Невозможно начать генерацию песни в текущем статусе.',
  SongCompressedTracksArtifactNotReadyError: 'Архив треков ещё не готов.',
  VoiceDispatchForbiddenError: 'Невозможно отправить голос на обработку в текущем состоянии.',
  VoiceAwaitDispatchingForbiddenError: 'Голос ещё ожидает отправки на обработку.',
  BalanceMustBePositiveError: 'Баланс должен быть положительным.',
  VoiceReadyForbiddenError: 'Голос не может быть переведён в готовое состояние сейчас.',
  VoiceFailForbiddenError: 'Невозможно пометить голос как неудачный в текущем состоянии.',
  VoiceIsNotPreparingError: 'Голос сейчас не находится в состоянии подготовки.',
  VoiceAiProviderNotSupportedError: 'Выбранный AI-провайдер голоса не поддерживается.',
  InsufficientBalanceError: 'Недостаточно средств. Пожалуйста, пополните баланс.',
  BalanceTransactionPendingForbiddenError: 'Транзакция уже находится в ожидании.',
  BalanceTransactionConfirmationForbiddenError: 'Подтвердить транзакцию в текущем состоянии нельзя.',
  BalanceTransactionFailureForbiddenError: 'Отклонить транзакцию в текущем состоянии нельзя.',
  PaymentStartForbiddenError: 'Начать платёж в текущем состоянии нельзя.',
  PaymentCheckForbiddenError: 'Проверить статус платежа в текущем состоянии нельзя.',
  PaymentCancellationForbiddenError: 'Отменить платёж в текущем состоянии нельзя.',
  PaymentConfirmationForbiddenError: 'Подтвердить платёж в текущем состоянии нельзя.',
  PaymentFailureForbiddenError: 'Зафиксировать ошибку платежа в текущем состоянии нельзя.',
  PurchaseEmptyError: 'Корзина покупок пуста.',
  PurchaseConfirmationForbiddenError: 'Невозможно подтвердить покупку в текущем статусе.',
  PurchasePendingForbiddenError: 'Покупка уже в обработке.',
  PurchaseCancellationForbiddenError: 'Невозможно отменить покупку в текущем статусе.',
  PurchaseFailureForbiddenError: 'Невозможно зафиксировать ошибку покупки в текущем статусе.',

  // Value objects
  ValueObjectError: 'Некорректные данные.',
  GenerationVariantsError: 'Некорректное количество вариантов генерации.',
  GenerationVariantsLimitReachedError: 'Достигнут лимит вариантов генерации.',
  GenerationVariantsNegativeError: 'Количество вариантов не может быть отрицательным.',
  TrackArtifactError: 'Ошибка артефакта трека.',
  TrackArtifactMustBeAudioError: 'Артефакт трека должен быть аудиофайлом.',
  TrackArchiveError: 'Ошибка архива трека.',
  TrackArchiveArtifactsMustBeDocumentError: 'Артефакты архива трека должны быть документами.',
  ArchivedTrackError: 'Ошибка архивированного трека.',
  ArchivedTrackMustBeDownloadableError: 'Архивированный трек недоступен для скачивания.',
  FileKeyError: 'Некорректный ключ файла.',
  FileKeyTooLongError: 'Ключ файла слишком длинный.',
  FileKeyInvalidFormatError: 'Неверный формат ключа файла.',
  VoiceArtifactError: 'Ошибка артефакта голоса.',
  VoiceArtifactMustBeAudioError: 'Артефакт голоса должен быть аудиофайлом.',
  VoiceArtifactDurationUnrecognizedError: 'Не удалось определить длительность аудио.',
  VoiceArtifactFormatUnrecognizedError: 'Не удалось определить формат аудио.',
  VoiceArtifactUnsupportedFormatError: 'Формат аудио не поддерживается.',
  VoiceArtifactTooShortError: 'Аудио слишком короткое.',
  VoiceArtifactTooLongError: 'Аудио слишком длинное.',
  VoiceArtifactTooBigError: 'Аудиофайл слишком большой.',
  AggregatedVoiceArtifactError: 'Ошибка проверки аудиофайла голоса.',
  UsernameError: 'Некорректное имя пользователя.',
  UsernameTooLongError: 'Имя пользователя слишком длинное.',
  UsernameTooShortError: 'Имя пользователя слишком короткое.',
  UsernameMustContainLettersError: 'Имя пользователя должно содержать хотя бы одну букву.',
  BalanceAmountError: 'Некорректная сумма баланса.',
  BalanceAmountNegativeError: 'Сумма баланса не может быть отрицательной.',
  MoneyError: 'Некорректная сумма.',
  NegativeMoneyAmountError: 'Сумма не может быть отрицательной.',
  TariffItemAmountError: 'Некорректное количество товара в тарифе.',
  TariffItemAmountInvalidError: 'Недопустимое количество товара в тарифе.',
  HexColorError: 'Некорректный цвет.',
  HexColorInvalidError: 'Неверный формат цвета.',
  GradientAngleError: 'Некорректный угол градиента.',
  GradientAngleNotInRangeError: 'Угол градиента находится вне допустимого диапазона.',
  GradientStopPositionError: 'Некорректная позиция точки градиента.',
  GradientStopPositionNotInRangeError: 'Позиция точки градиента вне допустимого диапазона.',
  TariffNameError: 'Некорректное название тарифа.',
  TariffNameTooShortError: 'Название тарифа слишком короткое.',
  TariffNameTooLongError: 'Название тарифа слишком длинное.',
  TariffShortDescriptionError: 'Некорректное краткое описание тарифа.',
  TariffShortDescriptionLengthNotInRangeError: 'Длина краткого описания тарифа недопустима.',
  GenerationTaskError: 'Ошибка задачи генерации.',
  SongError: 'Ошибка песни.',
  VoiceError: 'Ошибка голоса.',
  UserError: 'Ошибка пользователя.',
  BalanceError: 'Ошибка баланса.',
  BalanceTransactionError: 'Ошибка транзакции баланса.',
  TariffError: 'Ошибка тарифа.',
  PaymentError: 'Ошибка платежа.',
  PurchaseError: 'Ошибка покупки.',
  AnonymousUserError: 'Ошибка анонимного пользователя.',

  // App errors
  AppInfrastructureError: 'Внутренняя ошибка сервера. Попробуйте позже.',
  AudioProbeError: 'Не удалось обработать аудиофайл.',
  FileStorageError: 'Ошибка файлового хранилища.',
  FileStorageFileNotFoundError: 'Файл не найден в хранилище.',
  FilePermissionDeniedError: 'Нет прав для доступа к файлу.',
  FileIOError: 'Ошибка чтения/записи файла.',
  FileTooLargeError: 'Файл слишком большой.',
  FileKeyInvalidError: 'Недопустимый ключ файла.',
  FileStorageSessionError: 'Ошибка сессии файлового хранилища.',
  FileStorageSessionClosedError: 'Сессия файлового хранилища закрыта.',
  FileStorageSessionCommitError: 'Ошибка сохранения файла.',
  ArchiverError: 'Ошибка архивации файлов.',
  SongPreviewMakerError: 'Ошибка создания превью песни.',
  AppExternalServiceError: 'Ошибка внешнего сервиса. Попробуйте позже.',
  MusicGeneratorError: 'Ошибка сервиса генерации музыки.',
  MusicGeneratorDeserializeError: 'Ошибка декодирования ответа сервиса генерации.',
  MusicGeneratorNetworkError: 'Нет связи с сервисом генерации музыки.',
  MusicGeneratorTimeoutError: 'Сервис генерации музыки не отвечает. Попробуйте позже.',
  MusicGeneratorConnectionError: 'Не удалось подключиться к сервису генерации музыки.',
  MusicGeneratorSSLError: 'Ошибка безопасного соединения с сервисом генерации.',
  MusicGeneratorResponseError: 'Некорректный ответ сервиса генерации музыки.',
  MusicGeneratorBadRequestError: 'Некорректный запрос к сервису генерации музыки.',
  MusicGeneratorUnauthorizedError: 'Ошибка авторизации в сервисе генерации музыки.',
  MusicGeneratorForbiddenError: 'Доступ к сервису генерации музыки запрещён.',
  MusicGeneratorNotFoundError: 'Ресурс в сервисе генерации музыки не найден.',
  MusicGeneratorTooManyRequestsError: 'Слишком много запросов к сервису генерации. Попробуйте позже.',
  MusicGeneratorServerError: 'Внутренняя ошибка сервиса генерации музыки.',
  MusicGeneratorServiceUnavailableError: 'Сервис генерации музыки временно недоступен.',
  MusicGeneratorBusinessError: 'Бизнес-ошибка сервиса генерации музыки.',
  MusicGeneratorQuotaExceededError: 'Превышена квота сервиса генерации музыки. Попробуйте позже.',
  MusicGeneratorConcurrentRequestsLimitReachedError: 'Достигнут лимит одновременных запросов к сервису генерации.',
  MusicGeneratorTaskNotReadyError: 'Задача генерации музыки ещё не готова.',
  SongFileStreamerError: 'Ошибка стриминга файла песни.',
  PaymentMethodGatewayError: 'Ошибка платёжного шлюза.',
  UnsupportedPaymentMethodError: 'Данный способ оплаты не поддерживается.',
  InvalidInvoiceIdError: 'Некорректный идентификатор счёта.',
  InvoiceNotFoundError: 'Счёт не найден.',
  VoiceProcessorError: 'Ошибка обработки голоса.',
  VoiceProcessorRejectedError: 'Обработка голоса отклонена.',
  VoiceProcessorUnavailableError: 'Сервис обработки голоса недоступен.',
  VoiceProcessorQuotaExceededError: 'Превышена квота сервиса обработки голоса.',
  DataCorruptionError: 'Ошибка целостности данных. Обратитесь в поддержку.',
  AnonymousAppError: 'Ошибка приложения анонимного пользователя.',
  AnonymousNotAuthenticatedError: 'Анонимный пользователь не аутентифицирован.',
  AnonymousAlreadyAuthenticatedError: 'Анонимный пользователь уже аутентифицирован.',
  AnonymousAuthenticationError: 'Ошибка аутентификации анонимного пользователя.',
  InvalidAnonymousRecoveryError: 'Код восстановления неверный или больше недействителен.',
  AnonymousRecoveryCodeAlreadyExistsError: 'Код восстановления уже был выпущен.',
  UserAppError: 'Ошибка приложения при работе с пользователем.',
  UserAuthenticationError: 'Ошибка аутентификации.',
  UserAlreadyAuthenticatedError: 'Вы уже вошли в систему.',
  UserNotAuthenticatedError: 'Необходимо войти в систему.',
  InvalidCredentialsError: 'Неверный логин или пароль.',
  CredentialsValidationError: 'Некорректные учётные данные.',
  LoginError: 'Некорректный логин.',
  LoginTooShortError: 'Логин слишком короткий.',
  LoginTooLongError: 'Логин слишком длинный.',
  PasswordError: 'Некорректный пароль.',
  PasswordTooShortError: 'Пароль слишком короткий.',
  PasswordTooLongError: 'Пароль слишком длинный.',
  SortingError: 'Ошибка параметров сортировки.',
};

export function translateApiError(error: unknown, _context?: ErrorContext): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : 'Произошла неизвестная ошибка';
  }

  const { message: code, detail } = error.body;

  if (code === 'FileKeyTooLongError') {
    const max = getDetailNumber(detail, 'max_length', 255);
    return `Ключ файла слишком длинный (максимум ${max} символов).`;
  }

  if (code === 'WrongBalanceTypeError') {
    const expected = getDetailValue(detail, 'expected');
    return expected
      ? `Некорректный тип баланса. Ожидается ${String(expected)}.`
      : ERROR_MESSAGES[code];
  }

  if (code === 'LoginTooShortError') {
    const min = getDetailNumber(detail, 'min_length', 3);
    return `Логин слишком короткий (минимум ${min} символа).`;
  }
  if (code === 'LoginTooLongError') {
    const max = getDetailNumber(detail, 'max_length', 50);
    return `Логин слишком длинный (максимум ${max} символов).`;
  }
  if (code === 'PasswordTooShortError') {
    const min = getDetailNumber(detail, 'min_length', 8);
    return `Пароль слишком короткий (минимум ${min} символов).`;
  }
  if (code === 'PasswordTooLongError') {
    const max = getDetailNumber(detail, 'max_length', 128);
    return `Пароль слишком длинный (максимум ${max} символов).`;
  }
  if (code === 'UsernameTooShortError') {
    const min = getDetailNumber(detail, 'min_length', 2);
    return `Имя пользователя слишком короткое (минимум ${min} символа).`;
  }
  if (code === 'UsernameTooLongError') {
    const max = getDetailNumber(detail, 'max_length', 50);
    return `Имя пользователя слишком длинное (максимум ${max} символов).`;
  }


  return ERROR_MESSAGES[code] ?? code ?? 'Произошла неизвестная ошибка';
}

const LOGIN_FIELD_CODES = new Set([
  'LoginError',
  'LoginTooShortError',
  'LoginTooLongError',
  'UserAlreadyExistsError',
  'InvalidCredentialsError',
  'CredentialsValidationError',
]);
const PASSWORD_FIELD_CODES = new Set([
  'PasswordError',
  'PasswordTooShortError',
  'PasswordTooLongError',
  'InvalidCredentialsError',
  'CredentialsValidationError',
]);
const NAME_FIELD_CODES = new Set([
  'UsernameError',
  'UsernameTooLongError',
  'UsernameTooShortError',
  'UsernameMustContainLettersError',
]);

export function classifyAuthError(error: unknown): FieldErrors {
  if (!(error instanceof ApiError)) return { general: translateApiError(error) };

  const code = error.body.message;
  const message = translateApiError(error);

  if (code === 'InvalidCredentialsError' || code === 'CredentialsValidationError') {
    return { login: message };
  }
  if (NAME_FIELD_CODES.has(code)) return { name: message };
  if (LOGIN_FIELD_CODES.has(code)) return { login: message };
  if (PASSWORD_FIELD_CODES.has(code)) return { password: message };
  return { general: message };
}
